import React, { useEffect, useRef, useState } from "react"

import { format } from "date-fns"
import {
  CalendarDays,
  CalendarIcon,
  Calendar as CalendarIconLucide,
  FileSpreadsheet,
  Grid3X3,
  Plus,
  RotateCcw,
  Search,
} from "lucide-react"
import { useSearchParams } from "react-router-dom"

import RoleTeamGuard from "@/components/guards/RoleTeamGuard"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { ALL_SHERIFF_TYPES } from "@/constants/sheriffOptions"
import { useAuth } from "@/contexts/AuthContext"
import { useDepots } from "@/hooks/useDepots"
import { useFetchPeople } from "@/hooks/useFetchPeople"
import { useSubTeams } from "@/hooks/useSubTeams"
import { cn } from "@/lib/utils"
import { isDepotActiveOn } from "@/utils/depotValidity"
import { setOrDeleteParam } from "@/utils/searchParams"

import SimpleCreateBlocksModal from "../shift/SimpleCreateBlocksModal"
import DailyScheduleView, { type DailyScheduleViewRef } from "./DailyScheduleView"
import HRView, { type HRViewRef } from "./HRView"
import ListView, { type ListViewRef } from "./ListView"
import MonthlyScheduleView, { type MonthlyScheduleViewRef } from "./MonthlyScheduleView"
import SheriffKanbanBoard, { type SheriffKanbanBoardRef } from "./SheriffKanbanBoard"

export type ViewMode = "daily" | "weekly" | "monthly" | "list" | "hr"
const VALID_VIEW_MODES: ViewMode[] = ["daily", "weekly", "monthly", "list", "hr"]

// Resolve the view mode from the URL, falling back to "daily" for unknown values
// or views the current role can't access (DSPs cannot access HR), so a deep link
// can't leave a DSP on a blank HR view.
const resolveViewMode = (raw: string | null, isDSP: boolean): ViewMode => {
  if (raw && VALID_VIEW_MODES.includes(raw as ViewMode)) {
    if (raw === "hr" && isDSP) return "daily"
    return raw as ViewMode
  }
  return "daily"
}
export type ShiftStatus =
  | "planned"
  | "empty"
  | "clocked-in"
  | "clocked-out"
  | "overdue"
  | "no-show"
  | "cancelled"
  | "unnotified-absence"

// Note: Employer options are now loaded dynamically via useSubTeams hook

const typeOptions: readonly string[] = ALL_SHERIFF_TYPES

// Hardcoded areas (provided externally; do not fetch meta)
const areaOptionsDelivery: string[] = ["Rc1 Deliveries", "Rc2 Deliveries"]
const areaOptionsCollection: string[] = ["Collections"]
const areaOptionsSortation: string[] = ["Sortation"]
const areaOptionsInternal: string[] = [
  "Driver Ops",
  "Depot",
  "Fleet Team",
  "Cx",
  "Live Ops",
  "Network",
  "Driver Resourcing",
  "Des",
]
const INTERNAL_EXTRA_LOCATIONS = ["WFH", "Shoreditch Office"] as const

const getAreaOptions = (scheduleType: ScheduleViewProps["scheduleType"]): string[] => {
  switch (scheduleType) {
    case "delivery":
      return areaOptionsDelivery
    case "collection":
      return areaOptionsCollection
    case "sortation":
      return areaOptionsSortation
    case "internal":
      return areaOptionsInternal
  }
}

interface ScheduleViewProps {
  scheduleType: "delivery" | "collection" | "sortation" | "internal"
  selectedDate: Date
  onSelectedDateChange: (date: Date) => void
}

const getDefaultFilters = (
  scheduleType: ScheduleViewProps["scheduleType"],
  firstLocation?: string,
) => ({
  viewMode: "daily" as ViewMode,
  selectedLocation: firstLocation || "Silvertown Depot",
  selectedEmployer: "all",
  selectedType: "all",
  selectedAreas: getAreaOptions(scheduleType),
})

const ScheduleView: React.FC<ScheduleViewProps> = ({
  scheduleType,
  selectedDate,
  onSelectedDateChange,
}) => {
  const { user, claims } = useAuth()
  const { subTeamOptions } = useSubTeams()
  const setSelectedDate = onSelectedDateChange

  // The URL is the sole source of truth for filters so the view can be shared /
  // deep-linked and survives a refresh. When a param is absent we use the per-type default.
  const [searchParams, setSearchParams] = useSearchParams()

  // Check if user is DSP
  const isDSP = claims.user_role === "DSP"

  // Get employer options from sub-teams
  const employerOptions = React.useMemo(() => {
    return subTeamOptions.map((opt) => opt.value)
  }, [subTeamOptions])

  // Filter employer options based on user role
  const availableEmployerOptions = React.useMemo(() => {
    // Admins see all options
    if (claims.user_role === "Admin") {
      return employerOptions
    }
    // Managers only see their sub_teams
    if (claims.user_role === "Manager" && user?.sub_teams) {
      return employerOptions.filter((option) => user.sub_teams.includes(option))
    }
    // DSPs see no filter (it's hidden for them anyway)
    return []
  }, [claims.user_role, user?.sub_teams, employerOptions])

  // Load filters from the URL, falling back to defaults
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    resolveViewMode(searchParams.get("view"), isDSP),
  )

  // Fetch people for the schedule views
  const { data: peopleData, isLoading: peopleLoading } = useFetchPeople({
    statusFilter: "Active",
    limit: 10000, // Fetch all people at once
  })

  // Flatten all pages of people data
  const people = React.useMemo(() => {
    return peopleData?.pages.flatMap((page) => page.people) || []
  }, [peopleData])

  const { data: depots = [] } = useDepots()
  const selectedDateString = format(selectedDate, "yyyy-MM-dd")

  // Depots in use on the selected date, so closed or not-yet-open ones drop out of the dropdown
  const baseLocations = React.useMemo(
    () =>
      depots
        .filter((depot) => depot.name && isDepotActiveOn(depot, selectedDateString))
        .map((depot) => depot.name)
        .sort(),
    [depots, selectedDateString],
  )

  // Hardcoding WFH and Shoreditch Office(INTERNAL_EXTRA_LOCATIONS) locations, as there is no other way to get it given current implementation
  const availableLocations = React.useMemo(() => {
    if (scheduleType !== "internal") return baseLocations
    return Array.from(new Set([...baseLocations, ...INTERNAL_EXTRA_LOCATIONS])).sort()
  }, [baseLocations, scheduleType])

  // Get first location for default
  const firstLocation = availableLocations[0]
  const availableTeamOptions = React.useMemo(() => getAreaOptions(scheduleType), [scheduleType])

  const [selectedLocation, setSelectedLocation] = useState<string>(() => {
    // Empty string will be set by useEffect once locations are loaded
    return searchParams.get("location") || ""
  })

  // Keep selected location valid for the current schedule type
  React.useEffect(() => {
    if (!firstLocation) return
    if (!selectedLocation || !availableLocations.includes(selectedLocation)) {
      setSelectedLocation(firstLocation)
    }
  }, [firstLocation, selectedLocation, availableLocations])

  // Role and Type are manager-only filters (hidden for DSP). Ignore their URL
  // params for DSPs so a deep link can't apply a filter they can't see; the mirror
  // effect then strips the param from the URL (forced "all" is dropped).
  const [selectedEmployer, setSelectedEmployer] = useState<string>(() => {
    return isDSP ? "all" : searchParams.get("role") || "all"
  })

  const [selectedType, setSelectedType] = useState<string>(() => {
    return isDSP ? "all" : searchParams.get("type") || "all"
  })

  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "")
  const [showCreateBlocksModal, setShowCreateBlocksModal] = useState(false)
  const [shiftsCount, setShiftsCount] = useState(0)
  const [isMonthlyLoading, setIsMonthlyLoading] = useState(false)

  // Refs for export functionality
  const dailyViewRef = useRef<DailyScheduleViewRef>(null)
  const weeklyViewRef = useRef<SheriffKanbanBoardRef>(null)
  const monthlyViewRef = useRef<MonthlyScheduleViewRef>(null)
  const listViewRef = useRef<ListViewRef>(null)
  const hrViewRef = useRef<HRViewRef>(null)

  const [selectedAreas, setSelectedAreas] = useState<string[]>(() => {
    // The Team filter only ever selects "all" or a single area, persisted as the `team` param.
    const team = searchParams.get("team")
    if (team) {
      return team === "all" ? getAreaOptions(scheduleType) : [team]
    }
    return getDefaultFilters(scheduleType).selectedAreas
  })

  const selectedTeam = React.useMemo(() => {
    if (selectedAreas.length !== 1) return "all"
    return availableTeamOptions.includes(selectedAreas[0]) ? selectedAreas[0] : "all"
  }, [availableTeamOptions, selectedAreas])

  // Mirror the current filters into the URL (source of truth for deep links).
  // Defaults are dropped from the URL to keep shared links clean. A functional
  // updater is used so we never clobber params owned elsewhere (tab, date).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        setOrDeleteParam(next, "view", viewMode, "daily")
        setOrDeleteParam(next, "location", selectedLocation, firstLocation || "")
        setOrDeleteParam(next, "role", selectedEmployer, "all")
        setOrDeleteParam(next, "type", selectedType, "all")
        setOrDeleteParam(next, "team", selectedTeam, "all")
        setOrDeleteParam(next, "search", searchTerm, "")
        // Avoid a redundant update (e.g. on mount, writing back what we just read).
        return next.toString() === prev.toString() ? prev : next
      },
      { replace: true },
    )
  }, [
    viewMode,
    selectedLocation,
    selectedEmployer,
    selectedType,
    selectedTeam,
    searchTerm,
    firstLocation,
    setSearchParams,
  ])

  // Update areas when schedule type tab changes (only if not already set)
  useEffect(() => {
    const defaultAreas = getDefaultFilters(scheduleType).selectedAreas
    const validAreas = getAreaOptions(scheduleType)

    // Check if current areas are valid for this schedule type
    const hasValidAreas = selectedAreas.some((area) => validAreas.includes(area))
    if (!hasValidAreas) {
      setSelectedAreas(defaultAreas)
    }
  }, [scheduleType, selectedAreas])

  // Reset filters to default
  const handleResetFilters = () => {
    const defaults = getDefaultFilters(scheduleType)
    setViewMode(defaults.viewMode)
    setSelectedDate(new Date())
    setSelectedLocation(defaults.selectedLocation)
    setSelectedEmployer(defaults.selectedEmployer)
    setSelectedType(defaults.selectedType)
    setSelectedAreas(defaults.selectedAreas)
    setSearchTerm("")
  }

  const getViewTitle = () => {
    const viewMap = {
      daily: "Day Schedule View",
      weekly: "Week Schedule View",
      monthly: "Month Schedule View",
      list: "List Schedule View",
    }
    return viewMap[viewMode]
  }

  const handleBlockCreated = (_block: unknown) => {
    // Close the modal after successful creation
    setShowCreateBlocksModal(false)
  }

  // Determine default shift type based on schedule type
  const getDefaultShiftType = ():
    "RC1 Deliveries" | "RC2 Deliveries" | "Collections" | "Sortation" | "Internal" => {
    if (scheduleType === "collection") return "Collections"
    if (scheduleType === "sortation") return "Sortation"
    // Default to RC1 for delivery
    return "RC1 Deliveries"
  }

  return (
    <div className="space-y-4">
      {/* Filters and Actions */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-wrap gap-3 md:gap-6 items-center">
        {/* Location Filter */}
        <div className="w-full sm:w-auto">
          <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">
            Location
          </label>
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {availableLocations.map((location) => (
                <SelectItem key={location} value={location}>
                  {location}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Filter */}
        <div className="w-full sm:w-auto">
          <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full sm:w-[200px] justify-start text-left font-normal",
                  !selectedDate && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "MMM d") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Role Filter - Hidden for DSP */}
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <div className="w-full sm:w-auto">
            <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">
              Role
            </label>
            <Select value={selectedEmployer} onValueChange={setSelectedEmployer}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {availableEmployerOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </RoleTeamGuard>

        {/* Type Filter - Hidden for DSP */}
        <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
          <div className="w-full sm:w-auto">
            <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">
              Type
            </label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {typeOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </RoleTeamGuard>

        <div className="w-full sm:w-auto">
          <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">Team</label>
          <Select
            value={selectedTeam}
            onValueChange={(value) =>
              setSelectedAreas(value === "all" ? availableTeamOptions : [value])
            }
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {availableTeamOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* View Mode Toggle */}
        <div className="w-full sm:w-auto">
          <label className="text-sm font-medium mb-1 block text-palette-jungleGreen700">View</label>
          <Select value={viewMode} onValueChange={(value) => setViewMode(value as ViewMode)}>
            <SelectTrigger className="w-[180px] h-10">
              <SelectValue>
                <div className="flex items-center gap-2">
                  {viewMode === "daily" && (
                    <>
                      <CalendarIconLucide size={16} />
                      <span>Day</span>
                    </>
                  )}
                  {viewMode === "weekly" && (
                    <>
                      <Grid3X3 size={16} />
                      <span>Week</span>
                    </>
                  )}
                  {viewMode === "monthly" && (
                    <>
                      <CalendarDays size={16} />
                      <span>Month</span>
                    </>
                  )}
                  {viewMode === "list" && (
                    <>
                      <FileSpreadsheet size={16} />
                      <span>List</span>
                    </>
                  )}
                  {viewMode === "hr" && (
                    <>
                      <CalendarIcon size={16} />
                      <span>HR</span>
                    </>
                  )}
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">
                <div className="flex items-start gap-2">
                  <CalendarIconLucide size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">Day</div>
                    <div className="text-xs text-palette-jungleGreen500">
                      View blocks for a single day
                    </div>
                  </div>
                </div>
              </SelectItem>
              <SelectItem value="weekly">
                <div className="flex items-start gap-2">
                  <Grid3X3 size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">Week</div>
                    <div className="text-xs text-palette-jungleGreen500">
                      Kanban board for the week
                    </div>
                  </div>
                </div>
              </SelectItem>
              <SelectItem value="monthly">
                <div className="flex items-start gap-2">
                  <CalendarDays size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">Month</div>
                    <div className="text-xs text-palette-jungleGreen500">
                      Calendar view for the month
                    </div>
                  </div>
                </div>
              </SelectItem>
              <SelectItem value="list">
                <div className="flex items-start gap-2">
                  <FileSpreadsheet size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">List</div>
                    <div className="text-xs text-palette-jungleGreen500">
                      Tabular list of all blocks
                    </div>
                  </div>
                </div>
              </SelectItem>
              {!isDSP && (
                <SelectItem value="hr">
                  <div className="flex items-start gap-2">
                    <CalendarIcon size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">HR</div>
                      <div className="text-xs text-palette-jungleGreen500">
                        Human resources overview
                      </div>
                    </div>
                  </div>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Action Buttons */}
        <div className="ml-auto w-full sm:w-auto">
          <div className="mb-1 text-sm font-medium invisible" aria-hidden="true">
            Actions
          </div>
          <Button
            variant="outline"
            className="h-10 w-full sm:w-auto"
            onClick={handleResetFilters}
            title="Reset all filters to default"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset Filters
          </Button>
        </div>
      </div>

      {/* Schedule Content */}
      <div className="bg-white border border-palette-jungleGreen200 rounded-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-palette-jungleGreen900">
              {getViewTitle()} {viewMode !== "monthly" ? `(${shiftsCount})` : ""}
            </h2>
            {viewMode === "monthly" && isMonthlyLoading && (
              <div className="flex items-center gap-2 text-sm text-palette-jungleGreen600">
                <Spinner size="sm" />
                <span>Updating calendar...</span>
              </div>
            )}

            {/* Legend - Hidden for monthly view */}
            {viewMode !== "monthly" && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-palette-jungleGreen600">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-white border border-palette-jungleGreen200 rounded"></div>
                  <span>Planned</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-palette-jungleGreen100 border border-palette-jungleGreen200 rounded"></div>
                  <span>Open</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-blue-100 rounded"></div>
                  <span>Unfulfilled</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-300 border border-green-500 rounded"></div>
                  <span>In Progress</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-50 border border-green-200 rounded"></div>
                  <span>Completed</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-orange-100 border border-orange-300 rounded"></div>
                  <span>Overdue</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
                  <span>No Show</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-purple-100 border border-purple-300 rounded"></div>
                  <span>Cancelled</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-amber-100 border border-amber-300 rounded"></div>
                  <span>Unnotified Absence</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {viewMode !== "monthly" && (
              /* Search Bar */
              <div className="relative flex items-center gap-2">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-palette-jungleGreen500"
                  size={18}
                />
                <Input
                  className="pl-10 w-full sm:w-[200px]"
                  placeholder="Search by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (viewMode === "daily" && dailyViewRef.current) {
                  dailyViewRef.current.exportToCSV()
                } else if (viewMode === "weekly" && weeklyViewRef.current) {
                  weeklyViewRef.current.exportToCSV()
                } else if (viewMode === "monthly" && monthlyViewRef.current) {
                  monthlyViewRef.current.exportToCSV()
                } else if (viewMode === "list" && listViewRef.current) {
                  listViewRef.current.exportToCSV()
                } else if (viewMode === "hr" && hrViewRef.current) {
                  hrViewRef.current.exportToCSV()
                }
              }}
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              Export
            </Button>

            <RoleTeamGuard roles={["Admin", "Manager"]} fallback={null}>
              <Button
                className="bg-palette-jungleGreen800 hover:bg-palette-jungleGreen900 text-white"
                size="sm"
                onClick={() => {
                  setShowCreateBlocksModal(true)
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Blocks
              </Button>
            </RoleTeamGuard>
          </div>
        </div>

        <div className="p-4">
          {/* Show loading state while waiting for locations to load */}
          {peopleLoading || !selectedLocation ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <Spinner size="lg" />
                <p className="text-sm text-palette-jungleGreen600">Loading locations...</p>
              </div>
            </div>
          ) : (
            <>
              {viewMode === "daily" && (
                <DailyScheduleView
                  ref={dailyViewRef}
                  date={selectedDate}
                  scheduleType={scheduleType}
                  filters={{
                    location: selectedLocation,
                    employer: selectedEmployer,
                    type: selectedType,
                    areas: selectedAreas,
                  }}
                  searchTerm={searchTerm}
                  onCountChange={setShiftsCount}
                  people={people}
                  peopleLoading={peopleLoading}
                />
              )}
              {viewMode === "weekly" && (
                <SheriffKanbanBoard
                  ref={weeklyViewRef}
                  date={selectedDate}
                  scheduleType={scheduleType}
                  filters={{
                    location: selectedLocation,
                    employer: selectedEmployer,
                    type: selectedType,
                    areas: selectedAreas,
                  }}
                  searchTerm={searchTerm}
                  onCountChange={setShiftsCount}
                  people={people}
                  peopleLoading={peopleLoading}
                />
              )}
              {viewMode === "monthly" && (
                <MonthlyScheduleView
                  ref={monthlyViewRef}
                  date={selectedDate}
                  scheduleType={scheduleType}
                  filters={{
                    location: selectedLocation,
                    employer: selectedEmployer,
                    type: selectedType,
                    areas: selectedAreas,
                  }}
                  onDateClick={(clickedDate) => {
                    setSelectedDate(clickedDate)
                    setViewMode("daily")
                  }}
                  onLoadingChange={setIsMonthlyLoading}
                />
              )}
              {viewMode === "list" && (
                <ListView
                  ref={listViewRef}
                  date={selectedDate}
                  scheduleType={scheduleType}
                  filters={{
                    location: selectedLocation,
                    employer: selectedEmployer,
                    type: selectedType,
                    areas: selectedAreas,
                  }}
                  searchTerm={searchTerm}
                  onCountChange={setShiftsCount}
                  people={people}
                  peopleLoading={peopleLoading}
                />
              )}
              {viewMode === "hr" && !isDSP && (
                <HRView
                  ref={hrViewRef}
                  date={selectedDate}
                  scheduleType={scheduleType}
                  filters={{
                    location: selectedLocation,
                    employer: selectedEmployer,
                    type: selectedType,
                    areas: selectedAreas,
                  }}
                  searchTerm={searchTerm}
                  onCountChange={setShiftsCount}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Blocks Modal */}
      <SimpleCreateBlocksModal
        open={showCreateBlocksModal}
        onOpenChange={setShowCreateBlocksModal}
        onBlockCreated={handleBlockCreated}
        defaultLocation={selectedLocation}
        defaultShiftType={getDefaultShiftType()}
        defaultRole={selectedEmployer !== "all" ? selectedEmployer : undefined}
        people={people}
        peopleLoading={peopleLoading}
        defaultType={selectedType !== "all" ? selectedType : undefined}
      />
    </div>
  )
}

export default ScheduleView
